import React from 'react';
import {
  BarChart3,
  FileText,
  Brain,
  MessageSquare,
  LayoutDashboard,
  Grid3X3,
  Package
} from 'lucide-react';

type DeliverableType = 'chart' | 'analysis' | 'model' | 'recommendation' | 'dashboard' | 'framework';

interface Deliverable {
  title: string;
  type: DeliverableType;
  description: string;
  audience?: string;
  dependencies?: string[];
}

interface DeliverablesSectionProps {
  deliverables: Deliverable[];
  className?: string;
}

const DeliverableIcon: React.FC<{ type: DeliverableType }> = ({ type }) => {
  const iconClass = "w-5 h-5";

  switch (type) {
    case 'chart':
      return <BarChart3 className={`${iconClass} text-blue-600`} />;
    case 'analysis':
      return <FileText className={`${iconClass} text-purple-600`} />;
    case 'model':
      return <Brain className={`${iconClass} text-green-600`} />;
    case 'recommendation':
      return <MessageSquare className={`${iconClass} text-amber-600`} />;
    case 'dashboard':
      return <LayoutDashboard className={`${iconClass} text-red-600`} />;
    case 'framework':
      return <Grid3X3 className={`${iconClass} text-cyan-600`} />;
    default:
      return <FileText className={`${iconClass} text-neutral-600`} />;
  }
};

const TypeBadge: React.FC<{ type: DeliverableType }> = ({ type }) => {
  const styles: Record<DeliverableType, string> = {
    chart: 'bg-blue-50 text-blue-700 border-blue-200',
    analysis: 'bg-purple-50 text-purple-700 border-purple-200',
    model: 'bg-green-50 text-green-700 border-green-200',
    recommendation: 'bg-amber-50 text-amber-700 border-amber-200',
    dashboard: 'bg-red-50 text-red-700 border-red-200',
    framework: 'bg-cyan-50 text-cyan-700 border-cyan-200'
  };

  const labels: Record<DeliverableType, string> = {
    chart: 'Chart',
    analysis: 'Analysis',
    model: 'Model',
    recommendation: 'Recommendation',
    dashboard: 'Dashboard',
    framework: 'Framework'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${styles[type]}`}>
      {labels[type]}
    </span>
  );
};

const DeliverableCard: React.FC<{ deliverable: Deliverable; index: number }> = ({ deliverable, index }) => {
  return (
    <div className="flex gap-4 p-4 bg-white border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors">
      {/* Number & Icon */}
      <div className="flex flex-col items-center gap-2">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-neutral-100 text-neutral-700 font-semibold text-sm">
          {index + 1}
        </span>
        <DeliverableIcon type={deliverable.type} />
      </div>

      {/* Content */}
      <div className="flex-1 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-medium text-neutral-900">{deliverable.title}</h4>
          <TypeBadge type={deliverable.type} />
        </div>

        <p className="text-sm text-neutral-600">{deliverable.description}</p>

        {/* Audience */}
        {deliverable.audience && (
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <span className="font-medium">Audience:</span>
            <span>{deliverable.audience}</span>
          </div>
        )}

        {/* Dependencies */}
        {deliverable.dependencies && deliverable.dependencies.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-neutral-500">
            <span className="font-medium">Depends on:</span>
            <span>{deliverable.dependencies.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const DeliverablesSection: React.FC<DeliverablesSectionProps> = ({
  deliverables,
  className = ''
}) => {
  if (!deliverables || deliverables.length === 0) {
    return null;
  }

  // Group deliverables by type for summary
  const typeCounts = deliverables.reduce((acc, d) => {
    acc[d.type] = (acc[d.type] || 0) + 1;
    return acc;
  }, {} as Record<DeliverableType, number>);

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-neutral-900">Expected Deliverables</h3>
        </div>
        <span className="text-sm text-neutral-500">
          {deliverables.length} deliverable{deliverables.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Type Summary */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(typeCounts).map(([type, count]) => (
          <div key={type} className="flex items-center gap-1.5 px-2 py-1 bg-neutral-50 rounded text-xs">
            <DeliverableIcon type={type as DeliverableType} />
            <span className="text-neutral-700 capitalize">{count} {type}{count !== 1 ? 's' : ''}</span>
          </div>
        ))}
      </div>

      <p className="text-sm text-neutral-600 mb-4">
        These deliverables will be produced during the analysis to directly answer
        the strategic question and support decision-making.
      </p>

      {/* Deliverables List */}
      <div className="space-y-3">
        {deliverables.map((deliverable, index) => (
          <DeliverableCard key={index} deliverable={deliverable} index={index} />
        ))}
      </div>
    </div>
  );
};

export default DeliverablesSection;
